import { useEffect, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useCollapsedSidebar } from '@/lib/useCollapsedSidebar';
import { useActiveView } from '@/lib/useActiveView';
import { cn } from '@/lib/cn';
import { Sidebar } from './layout/Sidebar';
import { TopBar } from './layout/TopBar';

/**
 * The signed-in frame: a fixed header, a sidebar that is an icon rail on
 * desktop and a drawer on mobile, and the page.
 *
 * Both pieces of state live here rather than in the sidebar. The drawer has to
 * close when the route changes — one left open sits on top of the page it just
 * navigated to — and the content offset has to follow the rail's width; neither
 * is knowable from inside the sidebar.
 *
 * With no role there is no shell at all. Sign-in and the unauthorized page are
 * whole screens of their own, and wrapping them in navigation for a role the
 * person does not have would be both wrong and confusing.
 */
export function AppShell({ children }: { children: ReactNode }) {
  const { role, roles } = useAuth();
  const [view, setView] = useActiveView(roles);
  const [collapsed, setCollapsed] = useCollapsedSidebar();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();

  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  if (!role) return <>{children}</>;

  return (
    <div className="min-h-screen bg-surface">
      <TopBar
        roles={roles}
        view={view}
        onChangeView={setView}
        onOpenDrawer={() => setDrawerOpen(true)}
      />

      {drawerOpen ? (
        <div
          data-testid="drawer-backdrop"
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setDrawerOpen(false)}
        />
      ) : null}

      <Sidebar
        role={view}
        collapsed={collapsed}
        onToggleCollapsed={() => setCollapsed(!collapsed)}
        drawerOpen={drawerOpen}
        onCloseDrawer={() => setDrawerOpen(false)}
      />

      {/* The sidebar is fixed to the reading-start edge, so the content is
          padded away from it on desktop only — on mobile the drawer floats over
          the page and the content keeps the full width. Logical padding, so it
          follows the language. */}
      <div className={cn('pt-16 transition-all duration-300', collapsed ? 'lg:ps-[72px]' : 'lg:ps-64')}>
        <main className="p-4 md:p-8">{children}</main>
      </div>
    </div>
  );
}
